import React, {useEffect, useState} from "react";
import { Link } from "react-router-dom";
import {Container, Row, Col, Card, Table, Button, Modal} from "react-bootstrap";
import { useSuppliers } from "../SupplierContext";
import { usePurchases } from "../PurchaseContext";
import { useProducts } from "../ProductContext";

function Inventory() {
    // this component would ideally use state and effects to load the summary data
    const { suppliers, fetchAllSuppliers } = useSuppliers();
    const [suppliersWithCreditBalance, setSuppliersWithCreditBalance] = useState([]);
    const [totalOutstandingBalance, setTotalOutstandingBalance] = useState(0);

    const { purchases, fetchAllPurchases } = usePurchases();
    const [todaysPurchases, setTodaysPurchases] = useState([]);
    const [totalPurchasesValue, setTotalPurchasesValue] = useState(0);

    // state for purchase details modal
    const [showPurchaseModal, setShowPurchaseModal] = useState(false);
    const [selectedPurchase, setSelectedPurchase] = useState(null);

    // state for product details
    const {fetchAllProducts, totalProductsCount, fetchAllProductsForStockValue, fetchAllProductsForRecent,
    } = useProducts();
    const [totalStockValue, setTotalStockValue] = useState(0);
    const [recentProducts, setRecentProducts] = useState([]);


    useEffect(() => {
        fetchAllSuppliers();
        fetchAllPurchases();
        fetchAllProducts();
    }, [fetchAllSuppliers, fetchAllPurchases, fetchAllProducts]);

    useEffect(() => {
        if (suppliers && suppliers.length > 0) {
            // filter suppliers with credit balance
            const filteredSuppliers = suppliers.filter(supplier => parseFloat(supplier.balance) > 0);
            setSuppliersWithCreditBalance(filteredSuppliers);

            // calculate total outstanding balance
            const total = filteredSuppliers.reduce((acc, supplier) => acc + parseFloat(supplier.balance), 0);
            setTotalOutstandingBalance(total);
        }
    }, [suppliers]);

    // purchases logic
    useEffect(() => {
        if (purchases && purchases.length > 0) {
            // filter purchases for today
            const today = new Date().toISOString().split('T')[0];
            const filteredPurchases = purchases.filter(purchase => {
                const purchaseDate = purchase.purchase_date || purchase.created_at;
                return purchaseDate && purchaseDate.startsWith(today);
            });
            setTodaysPurchases(filteredPurchases);

            // calculate total purchases value
            const total = filteredPurchases.reduce((acc, purchase) => acc + parseFloat(purchase.total_amount), 0);
            setTotalPurchasesValue(total);
        }
    }, [purchases]);

    // total stock value
    useEffect(() => {
        const calculateTotalStockValue = async () => {
            const allProducts = await fetchAllProductsForStockValue();

            // Calculate total stock value
            const totalValue = allProducts.reduce((acc, product) => {
                return acc + parseFloat(product.stock) * parseFloat(product.cost_price);
            }, 0);

            setTotalStockValue(totalValue);

            // Update total products count (if needed)
            // setTotalProducts(allProducts.length);
        };

        calculateTotalStockValue().then(r => r);
    }, [fetchAllProductsForStockValue]);


    // recently added products
    useEffect(() => {
        const fetchRecentProducts = async () => {
            const allProducts = await fetchAllProductsForRecent();

            const sortedProducts = [...allProducts].sort((a, b) => {
                return new Date(b.created_at) - new Date(a.created_at);
            });

            setRecentProducts(sortedProducts.slice(0, 5));
        };

        fetchRecentProducts().then(r => r);
    }, [fetchAllProductsForRecent]);


    const handleShowPurchaseDetails = (purchase) => {
        setSelectedPurchase(purchase);
        setShowPurchaseModal(true);
    };

    const handleClosePurchaseModal = () => {
        setSelectedPurchase(null);
        setShowPurchaseModal(false);
    }

    return (
        <Container className="mt-5">
            <h1 className="text-center mb-4">Inventory Dashboard</h1>

            <Row className="mt-4">
                <Col>
                    <h2>Quick Access</h2>
                    <Link to="/inventory/products" className="btn btn-primary me-2 mb-2">Products</Link>
                    <Link to="/inventory/product-report" className="btn btn-outline-success me-2 mb-2">Product Report</Link>
                    <Link to="/inventory/purchases" className="btn btn-success me-2 mb-2">Purchases</Link>
                    <Link to="/inventory/suppliers" className="btn btn-warning me-2 mb-2">Suppliers</Link>
                    <Link to="/inventory/store-inventory" className="btn btn-outline-primary me-2 mb-2">Store Inventory</Link>
                    <Link to="/inventory/stock-ledger" className="btn btn-info me-2 mb-2">Stock Ledger</Link>
                    <Link to="/inventory/reorder-levels" className="btn btn-secondary me-2 mb-2">Reorder Levels</Link>
                    <Link to="/inventory/stock-transfer" className="btn btn-dark me-2 mb-2">Stock Transfer</Link>
                </Col>
            </Row>
            <Row>
                <Col md={4} className="mb-4">
                    <Card>
                        <Card.Body className="bg-primary text-white">
                            <Card.Title>Product Overview</Card.Title>
                            <h2 style={{ color: 'black' }}>Total Products: {totalProductsCount}</h2>
                            <h2 style={{ color: 'black' }}>Total Stock Value: {totalStockValue.toFixed(2)}</h2>
                            <div>
                                <h5>Recently Added Products</h5>
                                {recentProducts.length > 0 ? (
                                    <ul>
                                        {recentProducts.map(product => (
                                            <li key={product.id}>
                                                {product.product_name} - Added on {new Date(product.created_at).toLocaleDateString('en-GB')}
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p>No products added recently</p>
                                )}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4} className="mb-4">
                    <Card className="bg-success text-white">
                        <Card.Body>
                            <Card.Title>Purchases Overview</Card.Title>
                            <h2 style={{ fontWeight: 'bold' }}>
                                Today's Purchases Total: {totalPurchasesValue.toFixed(2)}
                            </h2>
                            <div>
                                <Table striped bordered hover variant="light">
                                    <thead>
                                        <tr>
                                            <th>Supplier Name</th>
                                            <th>Purchase Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {todaysPurchases.map(purchase => (
                                            <tr key={purchase.id}>
                                                <td>
                                                    <Button variant="link" onClick={() => handleShowPurchaseDetails(purchase)}>
                                                    {purchase.supplier_name}
                                                    </Button>
                                                </td>
                                                <td>{parseFloat(purchase.total_amount).toFixed(2)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4} className="mb-4">
                    <Card className="bg-warning">
                        <Card.Body>
                            <Card.Title>Suppliers Overview</Card.Title>
                            {/* display total outstanding value */}
                            <h2 style={{ color: 'red', fontWeight: 'bold' }}>
                                Total Outstanding: {totalOutstandingBalance.toFixed(2)}
                            </h2>
                            {/* supplier list*/}
                            <div>
                                <Table striped bordered hover>
                                    <thead>
                                        <tr>
                                            <th>Supplier</th>
                                            <th>Outstanding Balance</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {suppliersWithCreditBalance.map(supplier => (
                                            <tr key={supplier.id}>
                                                <td>{supplier.supplier_name}</td>
                                                <td>{parseFloat(supplier.balance).toFixed(2)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

             {/* Purchase Details Modal */}
            <Modal show={showPurchaseModal} onHide={handleClosePurchaseModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Purchase Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedPurchase && (
                        <>
                            <h5>Supplier: {selectedPurchase.supplier_name}</h5>
                            <h5>Date: {new Date(selectedPurchase.purchase_date || selectedPurchase.created_at).toLocaleDateString('en-GB')}</h5>
                            <h5>Invoice Number: {selectedPurchase.invoice_number}</h5>
                            <h5>Total Amount: {parseFloat(selectedPurchase.total_amount).toFixed(2)}</h5>
                            {/* Display purchase items if available */}
                            <Table striped bordered hover>
                                <thead>
                                    <tr>
                                        <th>Product</th>
                                        <th>Quantity</th>
                                        <th>Purchase Price</th>
                                        <th>Subtotal</th>
                                    </tr>
                                </thead>
                                <tbody>
                                      {selectedPurchase.items.map((item, index) => (
                                         <tr key={index}>
                                            <td>{item.product_name} {item.unit_name? `${item.unit_name}` : ''}</td>
                                            <td>{item.quantity}</td>
                                            <td>{parseFloat(item.purchase_price).toFixed(2)}</td>
                                            <td>{(item.quantity * item.purchase_price).toFixed(2)}</td>
                                         </tr>
                                      ))}
                                </tbody>
                            </Table>
                        </>
                    )}
                </Modal.Body>
            </Modal>
        </Container>
    );
}

export default Inventory;
